/**
 * PilasView — Sprint 19a RP-04 (rediseño venta en un solo paso).
 *
 * Tab "Pilas" del mantenedor /tesoreria/recursos. Componente puro.
 *
 * 2 secciones (antes 3, con el paso "Consumir" eliminado):
 * - Pilas en stock disponible: items tipo=pila, estado=en_stock (sin botón
 *   "Consumir" — ahora vender descuenta el stock directamente).
 * - Ventas recientes: items tipo=pila, estado=consumido, top N por
 *   consumido_at desc (cada una corresponde a una pila vendida/descontada).
 *
 * La acción única es "+ Vender pilas" (descuenta stock + genera el cobro).
 */
import type { StockRecursoRead } from '../../lib/api/types';
import { Button } from '../ui/Button';
import { formatFecha } from '../../lib/format';
import { Monto, Num } from './Monto';

interface Props {
  stockPila: StockRecursoRead[];
  ventasRecientes: StockRecursoRead[];
  loading: boolean;
  error: string | null;
  onVenderPilas: () => void;
}

export function PilasView({
  stockPila,
  ventasRecientes,
  loading,
  error,
  onVenderPilas,
}: Props) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="text-sm text-text-muted max-w-3xl">
          Pilas para los controles remotos. Al vender se descuenta el stock y
          se genera el cobro a la casa (queda como ingreso por cobrar); cuando
          el vecino transfiere, clasificas el pago. El control es referencia
          opcional: puedes vender pilas sueltas de repuesto.
        </div>
        <Button variant="primary" onClick={onVenderPilas}>
          + Vender pilas
        </Button>
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Sección 1 — Pilas en stock disponible */}
          <section className="space-y-2" data-testid="seccion-pilas-stock">
            <h3 className="text-xs uppercase tracking-wide text-text-secondary font-medium">
              Pilas en stock disponible ({stockPila.length})
            </h3>
            {stockPila.length === 0 ? (
              <p className="text-xs text-text-muted">
                Sin pilas en stock. Usa "+ Ingresar stock" en el tab Stock
                para reponer.
              </p>
            ) : (
              <div className="overflow-x-auto bg-bg-card border-[0.5px] border-white/[0.08] rounded-md">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.08]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">
                        Ingresada
                      </th>
                      <th className="text-right px-3 py-2 font-medium">
                        Costo
                      </th>
                      <th className="text-right px-3 py-2 font-medium">
                        Valor venta
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockPila.map((s) => (
                      <tr
                        key={s.id}
                        data-testid="fila-pila-stock"
                        className="border-b border-white/[0.04]"
                      >
                        <td className="px-3 py-2 text-text-muted">
                          {formatFecha(s.ingresado_at)}
                        </td>
                        <td className="px-3 py-2 text-right text-text-secondary">
                          <Monto value={s.costo_compra} />
                        </td>
                        <td className="px-3 py-2 text-right text-text-primary">
                          <Monto value={s.valor_venta} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Sección 2 — Ventas recientes */}
          <section className="space-y-2" data-testid="seccion-ventas-recientes">
            <h3 className="text-xs uppercase tracking-wide text-text-secondary font-medium">
              Ventas recientes ({ventasRecientes.length})
            </h3>
            {ventasRecientes.length === 0 ? (
              <p className="text-xs text-text-muted">Sin ventas recientes.</p>
            ) : (
              <div className="overflow-x-auto bg-bg-card border-[0.5px] border-white/[0.08] rounded-md">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.08]">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">
                        Vendida
                      </th>
                      <th className="text-left px-3 py-2 font-medium">
                        Control de referencia
                      </th>
                      <th className="text-right px-3 py-2 font-medium">
                        Valor venta
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasRecientes.map((s) => (
                      <tr
                        key={s.id}
                        data-testid="fila-pila-vendida"
                        className="border-b border-white/[0.04]"
                      >
                        <td className="px-3 py-2 text-text-muted">
                          {s.consumido_at
                            ? formatFecha(s.consumido_at.slice(0, 10))
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-text-secondary">
                          {s.recurso_id ? (
                            <Num>{s.recurso_identificador ?? '—'}</Num>
                          ) : (
                            'venta suelta'
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-text-primary">
                          <Monto value={s.valor_venta} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
